import {Injectable} from '@angular/core';
import {AuthService} from '../auth.service';
import {GroupModel, GroupQueryModel} from '../../models/school/group.model';
import {Observable} from 'rxjs';
import {UserModel} from '../../models/authentication/user.model';
import {StringExtensionModel} from '../../models/extensions/string-extension.model';
import {map} from 'rxjs/operators';
import {StudentModel} from 'src/app/models/authentication/student.model';
import {SchoolModel} from '../../models/school/school.model';
import {ObjectiveModel} from '../../models/edu-admin/objective.model';

export interface GroupYearContextResolution {
  _id: string;
  groupId: string;
  migrationStatus: string;
  migrationNotes: string;
}

@Injectable({
  providedIn: 'root'
})
export class SchoolAdminGroupService {

  constructor(private authSv: AuthService) {
  }

  fetchGroups(filter: GroupQueryModel = null, includeContext = false): Observable<GroupModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups';

    const query = filter ? { ...filter } : {};
    if (includeContext) { query['includeContext'] = true; }
    url += StringExtensionModel.queryString(query);

    return this.authSv.get(url).pipe(map((response) => {

      return GroupModel.generateModels(response.data);

    }));

  }

  fetchGroup(groupId: string, includeContext = false): Observable<GroupModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId + '/group';

    if (includeContext) {
      url += StringExtensionModel.queryString({ includeContext: true });
    }

    return this.authSv.get(url).pipe(map((response) => {

      return GroupModel.generateModel(response.data);

    }));
  }

  resolveGroupContext(groupId: string): Observable<GroupYearContextResolution> {

    const url = `/schools/${SchoolModel.getCurrent()._id}/groups/${groupId}/resolveContext`;

    return this.authSv.patch(url, {}).pipe(map((response) => {
      return response.data as GroupYearContextResolution;
    }));

  }

  /**
   * Only use the name value of the group to create it.
   * Uses the school associated with the currently logged in user to set other fields.
   *
   * @param group The new group to create.
   */
  createGroup(group: GroupModel): Observable<GroupModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups';

    return this.authSv.post(url, group).pipe(map((response) => {
      return GroupModel.generateModel(response.data);
    }));
  }

  /**
   * Only use the name field which can be updated
   * through this endpoint.
   *
   * @param group The group to update and it field.
   */
  updateGroup(group: GroupModel): Observable<GroupModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + group._id;

    return this.authSv.patch(url, group).pipe(map((response) => {

      return GroupModel.generateModel(response.data);

    }));
  }

  updateGroupGrade(groupId: string, grade: number): Observable<GroupModel> {

    const url = `/schools/${SchoolModel.getCurrent()._id}/groups/${groupId}`;

    return this.authSv.patch(url, { grade }).pipe(map((response) => {
      return GroupModel.generateModel(response.data);
    }));

  }

  updateGroupType(groupId: string, groupType: string): Observable<GroupModel> {

    const url = `/schools/${SchoolModel.getCurrent()._id}/groups/${groupId}`;

    return this.authSv.patch(url, { groupType }).pipe(map((response) => {
      return GroupModel.generateModel(response.data);
    }));

  }

  updateGroupQRCodeAuth(group: GroupModel, isQRCodeAuthEnabled: boolean): Observable<boolean> {

    let url = `/schools/${SchoolModel.getCurrent()._id}`;
    url += `/groups/${group._id}/QRCodeAuth`;

    return this.authSv.patch(url, { isQRCodeAuthEnabled }).pipe(map((response) => {
      return response.data;
    }));

  }

  updateGroupObjective(groupId: string, objectiveId: string): Observable<GroupModel> {

    return this.authSv.patch(`/schools/${SchoolModel.getCurrent()._id}/groups/${groupId}/updateObjective`,
      {
        objectiveId
      }).pipe(map((response) => {

        return GroupModel.generateModel(response.data);
    }));

  }

  deleteGroup(groupId: string, password: string): Observable<boolean> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/deleteGroup';

    return this.authSv.post(url, {password}).pipe(map((response) => {
      return response.data;

    }));

  }

  fetchGroupStudents(filter: GroupQueryModel = null, groupId: string): Observable<StudentModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId + '/students';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {

      return StudentModel.generateModels(response.data);

    }));
  }

  fetchStudentsWithNoRelationshipToGroup(groupId: string, filter: GroupQueryModel = null): Observable<StudentModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/notIncludedStudents';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {

      return StudentModel.generateModels(response.data);

    }));
  }

  addStudentsToGroup(groupId: string, studentIds: string[]): Observable<GroupModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/addStudents';

    return this.authSv.patch(url, { studentIds }).pipe(map((response) => {

      return GroupModel.generateModel(response.data);

    }));

  }

  removeStudentFromGroup(groupId: string, studentId: string): Observable<GroupModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/deleteStudent/' + studentId;

    return this.authSv.patch(url, {}).pipe(map((response) => {

      return GroupModel.generateModel(response.data);

    }));
  }

  removeStudentsFromGroup(groupId: string, studentIds: string[]): Observable<GroupModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/removeStudents';

    return this.authSv.patch(url, { studentIds }).pipe(map((response) => {

      return GroupModel.generateModel(response.data);

    }));

  }

  fetchGroupTeachers(filter: GroupQueryModel = null, groupId: string): Observable<UserModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId + '/teachers';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {

      return UserModel.generateList(response.data.teachers);

    }));
  }

  fetchTeachersWithNoRelationshipToGroup(groupId: string, filter: GroupQueryModel = null): Observable<UserModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/notIncludedTeachers';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {

      return UserModel.generateList(response.data.teachers);

    }));
  }

  addTeacherToGroup(groupId: string, teacherId: string): Observable<UserModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/addTeacher/' + teacherId;

    return this.authSv.patch(url, {}).pipe(map((response) => {

      return UserModel.generate(response.data);

    }));
  }

  removeTeacherFromGroup(groupId: string, teacherId: string): Observable<UserModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/groups/' + groupId;
    url += '/deleteTeacher/' + teacherId;

    return this.authSv.patch(url, {}).pipe(map((response) => {

      return UserModel.generate(response.data);

    }));
  }
}
